import React from 'react';
import { Button, Form, Icon, Label, Modal } from 'semantic-ui-react';
import { showError, showSuccess } from '../../helpers';

const modalityOptions = [
  { key: 'chat', value: 'chat', text: 'Chat' },
  { key: 'vision', value: 'vision', text: 'Vision' },
  { key: 'embedding', value: 'embedding', text: 'Embedding' },
  { key: 'audio', value: 'audio', text: 'Audio' },
  { key: 'realtime', value: 'realtime', text: 'Realtime' },
  { key: 'image', value: 'image', text: 'Image' },
  { key: 'video', value: 'video', text: 'Video' },
  { key: 'rerank', value: 'rerank', text: 'Rerank' },
  { key: 'other', value: 'other', text: 'Other' },
];

const terminalGatewayKeyPlaceholder = '<apiKey>,<type>,<card>';
const terminalGatewayKeyHint =
  'API Key 填写格式：<apiKey>,<type>,<card>。type 为 0 时 card 填 msisdn；type 为 1 时 card 填 iccid；type 为 2 时 card 填 imsi。';
const maasSeedanceBaseURL = 'https://zhenze-huhehaote.cmecloud.cn/api/v3';
const maasSeedanceModelName = 'doubao-seedance-2.0';
const maasSeedanceKeyHint =
  'API Key 填写移动云 MaaS API Key。若启用视频加密，请在服务端任务实现中准备 maas_public_key.pem 与 maas_private_key.pem。';
const seedanceRESTBaseURL = 'https://token.0a.com';
const seedanceRESTModelName = 'doubao-seedance-2.0';
const seedanceRESTVideoModelName = 'doubao-seedance-2.0-video';
const seedanceRESTKeyHint =
  'API Key 填写 Seedance REST Bearer Token；服务端会调用 /v1/video/generations 创建任务、查询状态并下载视频。';

const deepseekV31Params = JSON.stringify(
  {
    thinking: {
      type: 'enabled',
    },
    stream_options: {
      include_usage: true,
    },
    upstream: {
      provider: 'terminal-ai-gateway',
      path: '/compatible-mode/v1/chat/completions',
      authorization: 'Bearer <apiKey>,<type>,<card>',
    },
  },
  null,
  2
);

const maasSeedanceParams = JSON.stringify(
  {
    sdk: {
      language: 'go',
      package: 'maas_seedance_sdk_1.0.0_go',
      client: 'NewMaasSeedanceClient',
      methods: [
        'SetVideoFileEncryptKey',
        'CreateVideoGenerationTask',
        'QueryVideoGenerationTask',
        'DownloadVideo',
      ],
    },
    upstream: {
      provider: 'cmecloud-maas',
      base_url: maasSeedanceBaseURL,
      model: maasSeedanceModelName,
      enable_video_encrypt: true,
      public_key_path: 'maas_public_key.pem',
      private_key_path: 'maas_private_key.pem',
    },
    request_defaults: {
      generate_audio: true,
      ratio: '16:9',
      duration: 11,
      watermark: false,
      content_types: ['text', 'image_url', 'video_url', 'audio_url'],
    },
  },
  null,
  2
);

const seedanceRESTParams = JSON.stringify(
  {
    upstream: {
      provider: 'token-0a-seedance',
      base_url: seedanceRESTBaseURL,
      auth: 'Bearer <apiKey>',
      create: 'POST /v1/video/generations',
      query: 'GET /v1/video/generations/{task_id}',
      download: 'GET /v1/video/generations/{task_id}/content',
      models: [
        {
          name: seedanceRESTModelName,
          inputs: ['text', 'image_url'],
        },
        {
          name: seedanceRESTVideoModelName,
          inputs: ['text', 'image_url', 'video_url'],
        },
      ],
      usage_field: 'usage.total',
    },
    request_defaults: {
      generate_audio: true,
      ratio: '16:9',
      duration: 10,
      watermark: false,
      content_types: ['text', 'image_url', 'video_url'],
    },
  },
  null,
  2
);

function applyDeepseekV31Template(editing, updateEditing) {
  const fields = {
    display_name: 'DeepSeek-V3.1',
    provider: '中移 AI 融合网关',
    modality: 'chat',
    tags: 'deepseek,chat,thinking,openai-compatible',
    description:
      `按《终端厂商AI接口调用文档》1.1 文生文接入：OpenAI 兼容 POST /compatible-mode/v1/chat/completions。${terminalGatewayKeyHint}`,
    ratio: 0.001,
    completion_ratio: 1,
    default_params: deepseekV31Params,
    default_extra_params: JSON.stringify(
      {
        thinking: {
          type: 'enabled',
        },
        stream_options: {
          include_usage: true,
        },
      },
      null,
      2
    ),
    api_enabled: true,
    api_format: 'openai_compatible',
    api_base_url: 'https://api.tokens.cmonelink.com/compatible-mode/v1',
    api_model_name: '01-DeepSeek-V3.1',
  };
  if (!editing.managed) {
    fields.name = '01-DeepSeek-V3.1';
  }
  Object.entries(fields).forEach(([field, value]) => updateEditing(field, value));
}

function applyMaasSeedanceTemplate(editing, updateEditing) {
  const fields = {
    display_name: '豆包 Seedance 2.0',
    provider: '移动云 MaaS',
    modality: 'video',
    tags: 'seedance,video,maas,cmecloud,doubao',
    description:
      '按移动云 Seedance 视频生成 SDK 接入：NewMaasSeedanceClient(baseURL, apiKey, model, enableVideoEncrypt)，通过 CreateVideoGenerationTask、QueryVideoGenerationTask、DownloadVideo 创建、查询并下载视频。',
    ratio: 1,
    completion_ratio: 1,
    default_params: maasSeedanceParams,
    default_system_prompt: '',
    default_max_tokens: '',
    default_temperature: '',
    default_top_p: '',
    default_top_k: '',
    default_presence_penalty: '',
    default_frequency_penalty: '',
    default_stop: '',
    default_reasoning_effort: '',
    default_response_format: '',
    default_extra_params: '',
    api_enabled: true,
    api_format: 'maas_seedance',
    api_base_url: maasSeedanceBaseURL,
    api_model_name: maasSeedanceModelName,
  };
  if (!editing.managed) {
    fields.name = maasSeedanceModelName;
  }
  Object.entries(fields).forEach(([field, value]) => updateEditing(field, value));
}

function seedanceRESTUpstreamModelName(editing) {
  const currentName = (editing?.name || '').trim();
  if (currentName === seedanceRESTVideoModelName) {
    return seedanceRESTVideoModelName;
  }
  if (currentName === seedanceRESTModelName) {
    return seedanceRESTModelName;
  }
  const upstreamName = (editing?.api_model_name || '').trim();
  if (upstreamName === seedanceRESTVideoModelName) {
    return seedanceRESTVideoModelName;
  }
  return seedanceRESTModelName;
}

function applySeedanceRESTTemplate(editing, updateEditing) {
  const upstreamModelName = seedanceRESTUpstreamModelName(editing);
  const fields = {
    display_name: '豆包 Seedance 2.0',
    provider: 'Seedance REST',
    modality: 'video',
    tags: 'seedance,video,rest,doubao,token-0a',
    description:
      `按《Seedance API 文档》REST 接入：POST /v1/video/generations 创建视频任务，GET /v1/video/generations/{task_id} 查询状态，GET /v1/video/generations/{task_id}/content 下载视频；用量按 usage.total 读取。${seedanceRESTKeyHint}`,
    ratio: 1,
    completion_ratio: 1,
    default_params: seedanceRESTParams,
    default_system_prompt: '',
    default_max_tokens: '',
    default_temperature: '',
    default_top_p: '',
    default_top_k: '',
    default_presence_penalty: '',
    default_frequency_penalty: '',
    default_stop: '',
    default_reasoning_effort: '',
    default_response_format: '',
    default_extra_params: '',
    api_enabled: true,
    api_format: 'seedance_rest',
    api_base_url: seedanceRESTBaseURL,
    api_model_name: upstreamModelName,
  };
  if (!editing.managed) {
    fields.name = upstreamModelName;
  }
  Object.entries(fields).forEach(([field, value]) => updateEditing(field, value));
}

function isTerminalGatewayConfig(editing) {
  if (!editing) return false;
  return (
    (editing.api_base_url || '').includes('api.tokens.cmonelink.com') ||
    (editing.default_params || '').includes('terminal-ai-gateway')
  );
}

function isMaasSeedanceConfig(editing) {
  if (!editing) return false;
  return (
    editing.api_format === 'maas_seedance' ||
    (editing.api_base_url || '').includes('cmecloud.cn') ||
    (editing.default_params || '').includes('maas_seedance_sdk')
  );
}

function isSeedanceRESTConfig(editing) {
  if (!editing) return false;
  return (
    editing.api_format === 'seedance_rest' ||
    (editing.api_base_url || '').includes('token.0a.com') ||
    (editing.default_params || '').includes('token-0a-seedance')
  );
}

function formatJson(value) {
  if (!value || !value.trim()) return '';
  return JSON.stringify(JSON.parse(value), null, 2);
}

const JsonTextArea = ({ label, placeholder, field, editing, updateEditing }) => (
  <Form.Field>
    <label>{label}</label>
    <textarea
      placeholder={placeholder}
      rows={3}
      value={editing[field]}
      onChange={(e) => updateEditing(field, e.target.value)}
    />
    <div className='model-json-actions'>
      <Button
        type='button'
        size='mini'
        basic
        icon
        labelPosition='left'
        onClick={() => {
          try {
            updateEditing(field, formatJson(editing[field]));
            showSuccess('JSON 格式正确');
          } catch (error) {
            showError(`JSON 格式错误：${error.message}`);
          }
        }}
      >
        <Icon name='check' />
        校验/格式化
      </Button>
    </div>
  </Form.Field>
);

const ModelEditorModal = ({
  open,
  editing,
  saving,
  testing,
  onClose,
  onSave,
  onTest,
  updateEditing,
}) => (
  <Modal open={open} onClose={onClose} size='large' closeIcon>
    <Modal.Header>{editing.managed ? '编辑模型' : '新增模型'}</Modal.Header>
    <Modal.Content>
      <Form>
        <div className='model-form-section'>
          <h3>
            <Icon name='cube' />
            基础信息
          </h3>
          <div className='model-template-actions'>
            <Button
              type='button'
              size='small'
              basic
              color='black'
              icon
              labelPosition='left'
              onClick={() => applyDeepseekV31Template(editing, updateEditing)}
            >
              <Icon name='terminal' />
              应用 DeepSeek-V3.1 模板
            </Button>
            <Button
              type='button'
              size='small'
              basic
              color='blue'
              icon
              labelPosition='left'
              onClick={() => applyMaasSeedanceTemplate(editing, updateEditing)}
            >
              <Icon name='video camera' />
              应用移动云 Seedance 模板
            </Button>
            <Button
              type='button'
              size='small'
              basic
              color='teal'
              icon
              labelPosition='left'
              onClick={() => applySeedanceRESTTemplate(editing, updateEditing)}
            >
              <Icon name='film' />
              应用 Seedance REST 模板
            </Button>
          </div>
          <Form.Group widths='equal'>
            <Form.Input
              label='模型名称'
              placeholder='例如：gpt-4o-mini'
              value={editing.name}
              disabled={editing.managed}
              onChange={(e, { value }) => updateEditing('name', value)}
            />
            <Form.Input
              label='显示名称'
              placeholder='可选'
              value={editing.display_name}
              onChange={(e, { value }) => updateEditing('display_name', value)}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label='供应商'
              placeholder='例如：OpenAI / Qwen / 自建'
              value={editing.provider}
              onChange={(e, { value }) => updateEditing('provider', value)}
            />
            <Form.Select
              label='模型类型'
              options={modalityOptions}
              value={editing.modality}
              onChange={(_, { value }) => updateEditing('modality', value)}
            />
            <Form.Select
              label='状态'
              options={[
                { key: 'enabled', value: true, text: '启用' },
                { key: 'disabled', value: false, text: '停用' },
              ]}
              value={editing.enabled}
              onChange={(_, { value }) => updateEditing('enabled', value)}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label='模型倍率'
              type='number'
              min='0'
              step='0.0001'
              value={editing.ratio}
              onChange={(e, { value }) => updateEditing('ratio', value)}
            />
            <Form.Input
              label='补全倍率'
              type='number'
              min='0'
              step='0.0001'
              value={editing.completion_ratio}
              onChange={(e, { value }) => updateEditing('completion_ratio', value)}
            />
          </Form.Group>
          <Form.Input
            label='标签'
            placeholder='高性价比,视觉,企业'
            value={editing.tags}
            onChange={(e, { value }) => updateEditing('tags', value)}
          />
          <Form.TextArea
            label='说明'
            placeholder='记录模型用途、上游限制或运营备注'
            rows={3}
            value={editing.description}
            onChange={(e, { value }) => updateEditing('description', value)}
          />
        </div>

        <div className='model-form-section'>
          <h3>
            <Icon name='sliders horizontal' />
            默认参数
          </h3>
          <JsonTextArea
            label='默认参数 JSON'
            placeholder='{"temperature":0.2,"extra_params":{"safe_mode":true}}'
            field='default_params'
            editing={editing}
            updateEditing={updateEditing}
          />
          <Form.TextArea
            label='System Prompt'
            placeholder='未传 system 消息时自动补充'
            rows={3}
            value={editing.default_system_prompt}
            onChange={(e, { value }) => updateEditing('default_system_prompt', value)}
          />
          <Form.Group widths='equal'>
            <Form.Input
              label='Max Tokens'
              type='number'
              min='0'
              step='1'
              value={editing.default_max_tokens}
              onChange={(e, { value }) => updateEditing('default_max_tokens', value)}
            />
            <Form.Input
              label='Temperature'
              type='number'
              step='0.01'
              value={editing.default_temperature}
              onChange={(e, { value }) => updateEditing('default_temperature', value)}
            />
            <Form.Input
              label='Top P'
              type='number'
              step='0.01'
              value={editing.default_top_p}
              onChange={(e, { value }) => updateEditing('default_top_p', value)}
            />
            <Form.Input
              label='Top K'
              type='number'
              min='0'
              step='1'
              value={editing.default_top_k}
              onChange={(e, { value }) => updateEditing('default_top_k', value)}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label='Presence Penalty'
              type='number'
              step='0.01'
              value={editing.default_presence_penalty}
              onChange={(e, { value }) =>
                updateEditing('default_presence_penalty', value)
              }
            />
            <Form.Input
              label='Frequency Penalty'
              type='number'
              step='0.01'
              value={editing.default_frequency_penalty}
              onChange={(e, { value }) =>
                updateEditing('default_frequency_penalty', value)
              }
            />
            <Form.Input
              label='Reasoning Effort'
              placeholder='low / medium / high'
              value={editing.default_reasoning_effort}
              onChange={(e, { value }) =>
                updateEditing('default_reasoning_effort', value)
              }
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <JsonTextArea
              label='Stop JSON'
              placeholder='["END"]'
              field='default_stop'
              editing={editing}
              updateEditing={updateEditing}
            />
            <JsonTextArea
              label='Response Format JSON'
              placeholder='{"type":"json_object"}'
              field='default_response_format'
              editing={editing}
              updateEditing={updateEditing}
            />
          </Form.Group>
          <JsonTextArea
            label='自定义参数 JSON'
            placeholder='{"safe_mode":true}'
            field='default_extra_params'
            editing={editing}
            updateEditing={updateEditing}
          />
        </div>

        <div className='model-form-section'>
          <h3>
            <Icon name='plug' />
            三方 API
          </h3>
          <Form.Checkbox
            toggle
            label='启用三方 API'
            checked={editing.api_enabled}
            onChange={(_, { checked }) => updateEditing('api_enabled', checked)}
          />
          <Form.Group widths='equal'>
            <Form.Select
              label='API 格式'
              options={[
                {
                  key: 'openai_compatible',
                  value: 'openai_compatible',
                  text: 'OpenAI 兼容',
                },
                {
                  key: 'terminal_realtime_ws',
                  value: 'terminal_realtime_ws',
                  text: '终端 Realtime WebSocket',
                },
                {
                  key: 'maas_seedance',
                  value: 'maas_seedance',
                  text: '移动云 Seedance SDK',
                },
                {
                  key: 'seedance_rest',
                  value: 'seedance_rest',
                  text: 'Seedance REST',
                },
              ]}
              value={editing.api_format}
              onChange={(_, { value }) => updateEditing('api_format', value)}
            />
            <Form.Input
              label='分组'
              placeholder='default'
              value={editing.api_group}
              onChange={(e, { value }) => updateEditing('api_group', value)}
            />
          </Form.Group>
          <Form.Input
            label='API Base URL'
            placeholder='https://api.example.com/v1'
            value={editing.api_base_url}
            onChange={(e, { value }) => updateEditing('api_base_url', value)}
          />
          {editing.api_format === 'terminal_realtime_ws' ? (
            <div className='model-form-hint'>
              Realtime 调用路径：/compatible-mode/v1/realtime/dialogue，API Key
              填写 apikey,type,card。
            </div>
          ) : null}
          {editing.api_format === 'maas_seedance' ? (
            <div className='model-form-hint'>
              移动云 Seedance SDK 使用 Base URL、API Key、上游模型名初始化客户端；视频任务由 SDK 的 Create/Query/Download 方法处理。
            </div>
          ) : null}
          {editing.api_format === 'seedance_rest' ? (
            <div className='model-form-hint'>
              Seedance REST 使用 Bearer Token 调用 /v1/video/generations 创建、查询和下载视频任务；成功后按 usage.total 作为模型用量。
            </div>
          ) : null}
          <Form.Group widths='equal'>
            <Form.Input
              label='API Key'
              type='password'
              disabled={editing.api_key_clear}
              placeholder={
                editing.api_key_set
                  ? '留空保留已保存密钥'
                  : isTerminalGatewayConfig(editing)
                  ? terminalGatewayKeyPlaceholder
                  : isMaasSeedanceConfig(editing)
                    ? 'your_apikey'
                    : isSeedanceRESTConfig(editing)
                      ? 'sk-...'
                      : 'sk-...'
              }
              value={editing.api_key}
              onChange={(e, { value }) => updateEditing('api_key', value)}
            />
            <Form.Input
              label='上游模型名称'
              placeholder='留空使用当前模型名称'
              value={editing.api_model_name}
              onChange={(e, { value }) => updateEditing('api_model_name', value)}
            />
          </Form.Group>
          <div className='model-key-row'>
            {editing.api_key_set ? (
              <Label basic color='green'>
                API Key 已设置
              </Label>
            ) : (
              <Label basic>API Key 未设置</Label>
            )}
            {editing.api_key_set ? (
              <Form.Checkbox
                label='清除已保存密钥'
                checked={editing.api_key_clear}
                onChange={(_, { checked }) => {
                  updateEditing('api_key_clear', checked);
                  if (checked) updateEditing('api_key', '');
                }}
              />
            ) : null}
          </div>
          {isTerminalGatewayConfig(editing) ? (
            <div className='model-form-hint'>{terminalGatewayKeyHint}</div>
          ) : null}
          {isSeedanceRESTConfig(editing) ? (
            <div className='model-form-hint'>{seedanceRESTKeyHint}</div>
          ) : null}
          {isMaasSeedanceConfig(editing) ? (
            <div className='model-form-hint'>{maasSeedanceKeyHint}</div>
          ) : null}
          {editing.api_channel_id ? (
            <div className='model-form-hint'>已绑定通道 #{editing.api_channel_id}</div>
          ) : null}
        </div>
      </Form>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onClose}>取消</Button>
      <Button
        basic
        color='blue'
        loading={testing}
        disabled={!editing.api_base_url}
        onClick={onTest}
      >
        测试三方 API
      </Button>
      <Button primary loading={saving} onClick={onSave}>
        保存模型
      </Button>
    </Modal.Actions>
  </Modal>
);

export default ModelEditorModal;
